import { useCallback, useEffect, useRef, useState } from 'react';
import { DEBOUNCE_MS } from '@constants/timing';
import { useLiveSearchParams } from './useLiveSearchParams';

/** URL keys of a list position: dropped whenever a filter changes. */
const PAGE_KEYS = ['offset', 'page'] as const;

/**
 * Search box state that follows ?q= and writes back debounced, against the URL as it is when the timer fires
 * (useLiveSearchParams: a filter picked meanwhile stays). Nothing is written when ?key= already says it. A new search
 * drops `resetKeys` (the page position: ?offset=, or a cursor list's keys).
 */
export function useDebouncedParam(key: string, ms: number = DEBOUNCE_MS.urlSearch, resetKeys: readonly string[] = PAGE_KEYS) {
  const [params, setParams] = useLiveSearchParams();
  const fromUrl = params.get(key) ?? '';
  const [value, setValue] = useState(fromUrl);
  // Back/Forward or a link changed ?key=: show it, unless it is what this box wrote itself
  const [seen, setSeen] = useState(fromUrl);
  const written = useRef<string | null>(null);
  if (fromUrl !== seen) {
    setSeen(fromUrl);
    if (fromUrl !== written.current && fromUrl !== value.trim()) setValue(fromUrl);
  }
  useEffect(() => {
    const t = window.setTimeout(() => {
      const want = value.trim();
      written.current = want;
      // already in the URL (the mount, or a Back to it): no navigation that could carry an older URL
      if ((new URLSearchParams(window.location.search).get(key) ?? '') === want) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (want) next.set(key, want);
          else next.delete(key);
          if ((prev.get(key) ?? '') !== (next.get(key) ?? '')) for (const k of resetKeys) next.delete(k);
          return next;
        },
        { replace: true },
      );
    }, ms);
    return () => window.clearTimeout(t);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return [value, setValue] as const;
}

/** ?page= of an offset list (1 when missing or not a page number); a page step is a history entry (Back works). */
export function usePageParam() {
  const [params, setParams] = useLiveSearchParams();
  const n = Number(params.get('page'));
  const page = Number.isInteger(n) && n > 1 ? n : 1;
  const setPage = useCallback(
    (p: number, replace = false) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (p > 1) next.set('page', String(p));
          else next.delete('page');
          return next;
        },
        { replace },
      ),
    [setParams],
  );
  return [page, setPage] as const;
}

/** A page past the end (its rows were deleted, or changed so they no longer match, while open) jumps to the last one. */
export function useClampPage(meta: { totalPages: number } | null | undefined, page: number, setPage: (p: number, replace?: boolean) => void) {
  const last = meta?.totalPages;
  useEffect(() => {
    if (last && page > last) setPage(last, true);
  }, [page, last, setPage]);
}

/** List filters kept in the URL: changing one goes back to the first page (and replaces the history entry). */
export function useFilters() {
  const [params, setParams] = useLiveSearchParams();
  /** Several keys in one history step (two `set` calls in a row would overwrite each other). */
  const setMany = (changes: Record<string, string>) => {
    // a page step is a history entry; a new filter replaces it and starts at the first page
    const paging = Object.keys(changes).every((k) => (PAGE_KEYS as readonly string[]).includes(k));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        if (!paging) for (const p of PAGE_KEYS) next.delete(p);
        return next;
      },
      { replace: !paging },
    );
  };
  const set = (k: string, v: string) => setMany({ [k]: v });
  const clear = () => setParams(new URLSearchParams(), { replace: true });
  return { params, set, setMany, clear };
}

/**
 * A page past the end (its rows were deleted, or changed so they no longer match, while it was open) jumps
 * back to the last page that has rows, instead of saying the list is empty.
 */
export function useClampOffset(page: { total: number; items: unknown[] } | null, offset: number, limit: number) {
  const [, setParams] = useLiveSearchParams();
  useEffect(() => {
    if (!page || page.items.length || offset === 0) return;
    const last = page.total ? Math.floor((page.total - 1) / limit) * limit : 0;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (last) next.set('offset', String(last));
        else next.delete('offset');
        return next;
      },
      { replace: true },
    );
  }, [page, offset, limit, setParams]);
}
